import { CHAT_MAX_LENGTH, type ChatItemDto } from '@spin/shared';
import { useEffect, useRef, useState } from 'react';
import { game } from '../api/game';
import { clientId, http } from '../api/http';
import { Avatar } from '../components/common';
import { Emoji, EmojiText } from '../components/Emoji';
import { GiftBoxIcon, PuzzleIcon, SendIcon } from '../components/icons';
import { reportError, useT } from '../i18n';
import { useRoom } from '../store/room';
import { useSession } from '../store/session';
import { useUi } from '../store/ui';
import { sfx } from './sounds';

/** Marks a translation that is on its way. */
const PENDING = '\u0000';

/** The table chat under the board and the message bar ("Write a message here", stickers, gifts, send). */
export function ChatPanel() {
  const t = useT();
  const chat = useRoom((s) => s.chat);
  const me = useSession((s) => s.me)!;
  const canTranslate = useSession((s) => s.config?.translation ?? false);
  const { openSheet, openDialog } = useUi.getState();
  const [text, setText] = useState('');
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    const last = chat.at(-1);
    if (last && last.userId !== me.id && last.kind !== 'SYSTEM') sfx.message(me.soundVolume / 200);
  }, [chat, me.id, me.soundVolume]);

  const translate = async (id: string) => {
    if (translations[id] === PENDING) return;
    if (translations[id]) {
      setTranslations(({ [id]: _, ...rest }) => rest);
      return;
    }
    setTranslations((all) => ({ ...all, [id]: PENDING }));
    try {
      const res = await http.post<{ text: string }>('/chat/translate', { messageId: id });
      setTranslations((all) => ({ ...all, [id]: res.text }));
    } catch (err) {
      setTranslations(({ [id]: _, ...rest }) => rest);
      reportError(err);
    }
  };

  const send = async () => {
    const value = text.trim();
    if (!value) return;
    setText('');
    try {
      await game.emit('chat.send', { clientMsgId: clientId(), text: value });
    } catch (err) {
      setText(value);
      reportError(err);
    }
  };

  const render = (item: ChatItemDto) => {
    const meta = (item.meta ?? {}) as Record<string, unknown>;
    if (item.kind === 'SYSTEM') {
      const key = typeof meta.i18n === 'string' ? meta.i18n : null;
      const vars = Object.fromEntries(
        Object.entries((meta.vars ?? {}) as Record<string, unknown>).map(([k, v]) => [
          k,
          typeof v === 'string' && v.startsWith('@') ? t(v.slice(1)) : String(v),
        ]),
      );
      return (
        <div key={item.id} className="chat-event">
          <EmojiText text={key ? t(key, vars) : item.text} />
        </div>
      );
    }
    const open = () => item.userId && openSheet('player', { userId: item.userId });
    const vip = Boolean(item.vip ?? meta.vip);
    const translation = translations[item.id];
    return (
      <div
        key={item.id}
        className="msg"
        data-testid={item.kind === 'STICKER' ? 'chat-sticker' : 'chat-message'}
      >
        <button className="msg-avatar" onClick={open} aria-label={item.name ?? ''}>
          <Avatar name={item.name ?? '?'} photoUrl={item.photoUrl} size="100%" />
        </button>
        <div className="msg-bubble">
          <button className={vip ? 'msg-name vip' : 'msg-name'} onClick={open}>
            {item.name}
            {item.kind === 'TEXT' && ':'}
          </button>
          {item.kind === 'STICKER' ? (
            <div className="msg-sticker">
              <Emoji e={item.text} />
            </div>
          ) : (
            <>
              <span className="msg-text"> {item.text}</span>
              {translation && translation !== PENDING && (
                <span className="msg-translation" data-testid="translation">
                  {translation}
                </span>
              )}
              {canTranslate && item.userId !== me.id && (
                <button className="link-btn" onClick={() => void translate(item.id)}>
                  {translation === PENDING
                    ? t('table.translating')
                    : translation
                      ? t('table.hide_translation')
                      : t('table.show_translation')}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="chat" ref={listRef} data-testid="chat">
        {chat.map(render)}
      </div>
      <form
        className="composer"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <input
          value={text}
          maxLength={CHAT_MAX_LENGTH}
          placeholder={t('table.chat_placeholder')}
          onChange={(e) => setText(e.target.value)}
          enterKeyHint="send"
          data-testid="chat-input"
        />
        <button
          type="button"
          className="composer-icon"
          onClick={() => openSheet('stickers')}
          aria-label={t('stickers.title')}
          title={t('stickers.title')}
          data-testid="open-stickers"
        >
          <PuzzleIcon />
        </button>
        <button
          type="button"
          className="composer-icon"
          onClick={() => openDialog('bonus')}
          aria-label={t('activity.title')}
          title={t('activity.title')}
          data-testid="open-bonus"
        >
          <GiftBoxIcon size={29} />
        </button>
        <button className="composer-send" type="submit" aria-label={t('common.send')} data-testid="chat-send">
          <SendIcon />
        </button>
      </form>
    </>
  );
}
